/**
 * Navigation Bar Component
 *
 * TradingView-style flat toolbar with:
 * - Brand + symbol on the left
 * - Timeframe dropdown, timezone toggle, date picker
 * - Settings cog with candlestick color dialog
 * - Action buttons on the right
 */

import { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import CONFIG from '../config';
import { snapToWeekday, stepWeekday, getLatestSessionDate } from '../utils/market';
import TimezoneSelector from './TimezoneSelector';
import './NavigationBar.css';

const Separator = () => <div className="nav-sep" />;

const iconProps = {
  width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
};

const GearIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const BookIcon = () => (
  <svg {...iconProps}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const WrenchIcon = () => (
  <svg {...iconProps}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const CaretIcon = () => (
  <svg {...iconProps} width={11} height={11}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg {...iconProps} width={12} height={12}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function TimeframeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const current = CONFIG.chartTypes.find(ct => ct.value === value);
  const sections = [
    { title: 'Time-Based', items: CONFIG.chartTypes.filter(ct => ct.type === 'time') },
    { title: 'Volume-Based', items: CONFIG.chartTypes.filter(ct => ct.type === 'volume') },
  ];

  return (
    <div className="nav-dd" ref={ref}>
      <button
        className={`nav-dd-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen(prev => !prev)}
        title="Timeframe"
      >
        <span>{current?.label ?? value}</span>
        <span className="nav-dd-caret"><CaretIcon /></span>
      </button>
      {open && (
        <div className="nav-dd-menu">
          {sections.map(section => (
            <div key={section.title}>
              <div className="nav-dd-section">{section.title}</div>
              {section.items.map(ct => (
                <button
                  key={ct.value}
                  className={`nav-dd-item${ct.value === value ? ' active' : ''}`}
                  onClick={() => { onChange?.(ct.value); setOpen(false); }}
                >
                  <span>{ct.label}</span>
                  {ct.value === value && <CheckIcon />}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ColorRow({ label, color, onChange }) {
  return (
    <div className="nav-dialog-row">
      <span className="nav-dialog-label">{label}</span>
      <input
        type="color"
        className="nav-color"
        value={color}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function OpacityRow({ label, opacity, onChange }) {
  const pct = Math.round((opacity ?? 1) * 100);
  return (
    <div className="nav-dialog-row">
      <span className="nav-dialog-label">{label}</span>
      <input
        type="range"
        className="nav-range"
        min={0}
        max={100}
        step={5}
        value={pct}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
      />
      <span className="nav-range-value">{pct}%</span>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="nav-dialog-row">
      <span className="nav-dialog-label">{label}</span>
      <button
        className={`nav-switch${checked ? ' on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span className="nav-switch-knob" />
      </button>
    </div>
  );
}

function CandleSettingsDialog({ candleColors, onCandleColorsChange, displayType, onDisplayTypeChange, hideGridLines, onHideGridLinesChange, onClearSettings, indicators, onIndicatorToggle, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const update = (key, value) => {
    onCandleColorsChange({ ...candleColors, [key]: value });
  };

  const reset = () => {
    onCandleColorsChange({
      upColor: '#22c55e',
      downColor: '#ef4444',
      opacity: 1,
    });
  };

  return (
    <div ref={dialogRef} className="nav-dialog">
      <span className="nav-dialog-title">Candle Colors</span>

      <ColorRow label="Up (bullish)" color={candleColors.upColor} onChange={(v) => update('upColor', v)} />
      <ColorRow label="Down (bearish)" color={candleColors.downColor} onChange={(v) => update('downColor', v)} />
      <OpacityRow label="Opacity" opacity={candleColors.opacity} onChange={(v) => update('opacity', v)} />

      <button onClick={reset} className="nav-ghost-btn">
        Reset colors
      </button>

      <div className="nav-dialog-divider" />

      <span className="nav-dialog-title">Chart Type</span>

      <div className="nav-seg block">
        {[{ value: 'candle', label: 'Candle' }, { value: 'line', label: 'Line' }].map(opt => (
          <button
            key={opt.value}
            className={displayType === opt.value ? 'active' : ''}
            onClick={() => onDisplayTypeChange?.(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="nav-dialog-divider" />

      <span className="nav-dialog-title">Display</span>

      <ToggleRow
        label="Hide Tooltip"
        checked={indicators?.tooltipHidden || false}
        onChange={(v) => onIndicatorToggle?.('tooltipHidden', v)}
      />

      <ToggleRow
        label="Pin Tooltip"
        checked={indicators?.tooltipPinned || false}
        onChange={(v) => onIndicatorToggle?.('tooltipPinned', v)}
      />

      <ToggleRow
        label="Hide Grid Lines"
        checked={hideGridLines || false}
        onChange={(v) => onHideGridLinesChange?.(v)}
      />

      {onClearSettings && (
        <>
          <div className="nav-dialog-divider" />
          <button
            onClick={() => { onClearSettings(); onClose(); }}
            className="nav-ghost-btn danger"
          >
            Clear all settings
          </button>
        </>
      )}
    </div>
  );
}

function NavigationBar({
  date,
  timeframe,
  timezone,
  indicators = {},
  connected,
  onDateChange,
  onTimeframeChange,
  onTimezoneChange,
  onIndicatorToggle,
  userRole = null,
  onAdminToggle = null,
  onDocsToggle = null,
  username = null,
  onLogout = null,
  candleColors = { upColor: '#22c55e', downColor: '#ef4444' },
  onCandleColorsChange = null,
  displayType = 'candle',
  onDisplayTypeChange = null,
  hideGridLines = false,
  onHideGridLinesChange = null,
  onClearSettings = null,
}) {
  const [showSettings, setShowSettings] = useState(false);

  return (
    <nav className="nav-bar">
      {/* Brand + Symbol */}
      <div className="nav-brand">
        <span className="nav-wordmark"><span className="nav-wordmark-x">X</span>posureFlow</span>
        <span className="nav-symbol">ES</span>
        <span
          className={`nav-dot${connected ? ' live' : ''}`}
          title={connected ? 'Live data connected' : 'Disconnected'}
        />
      </div>

      <Separator />

      {/* Timeframe Selector */}
      <TimeframeDropdown value={timeframe} onChange={onTimeframeChange} />

      <Separator />

      {/* Timezone Toggle */}
      <TimezoneSelector timezone={timezone} onTimezoneChange={onTimezoneChange} />

      <Separator />

      {/* Date Picker with prev/next session arrows + Today */}
      <div className="nav-date-group">
        <button className="nav-date-btn" title="Earlier session"
          onClick={() => onDateChange?.(stepWeekday(date, -1))}>‹</button>
        <input
          type="date"
          className="nav-date"
          value={date}
          onChange={(e) => {
            const d = snapToWeekday(e.target.value);
            if (d) onDateChange?.(d);
          }}
        />
        <button className="nav-date-btn" title="Later session"
          onClick={() => { const n = stepWeekday(date, 1); const latest = getLatestSessionDate(); onDateChange?.(n > latest ? latest : n); }}>›</button>
        <button className="nav-date-btn nav-date-today" title="Latest session"
          onClick={() => onDateChange?.(getLatestSessionDate())}>Today</button>
      </div>

      <Separator />

      {/* Chart-view links — grouped segmented control (highlights the active view) */}
      <div className="nav-seg nav-charts">
        <NavLink to="/chart/planning" className="nav-seg-link" title="Planning chart (30m profiles)">
          Planning
        </NavLink>
        <NavLink to="/chart/renko" className="nav-seg-link" title="Renko Rotations (rotations + 70/90 leg-size bands)">
          Renko
        </NavLink>
        <NavLink to="/chart/api-docs" className="nav-seg-link" title="API reference (Swagger, uses your session)">
          API
        </NavLink>
      </div>

      {/* Spacer pushes actions to the right */}
      <div style={{ flex: 1 }} />

      {/* Action Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        {/* Settings Cog */}
        {onCandleColorsChange && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowSettings(prev => !prev)}
              title="Chart Settings"
              className={`nav-icon-btn${showSettings ? ' active' : ''}`}
            >
              <GearIcon />
            </button>
            {showSettings && (
              <CandleSettingsDialog
                candleColors={candleColors}
                onCandleColorsChange={onCandleColorsChange}
                displayType={displayType}
                onDisplayTypeChange={onDisplayTypeChange}
                hideGridLines={hideGridLines}
                onHideGridLinesChange={onHideGridLinesChange}
                onClearSettings={onClearSettings}
                indicators={indicators}
                onIndicatorToggle={onIndicatorToggle}
                onClose={() => setShowSettings(false)}
              />
            )}
          </div>
        )}

        {onDocsToggle && (
          <button onClick={onDocsToggle} title="Documentation" className="nav-icon-btn">
            <BookIcon />
          </button>
        )}

        {userRole === 'admin' && onAdminToggle && (
          <button onClick={onAdminToggle} title="Admin Panel" className="nav-icon-btn">
            <WrenchIcon />
          </button>
        )}

        {onLogout && (
          <>
            <Separator />
            <span className="nav-user">{username || 'user'}</span>
            <button onClick={onLogout} title="Logout" className="nav-icon-btn nav-logout">
              Logout
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

export default NavigationBar;
